import { Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import {
    ConfigState,
    selectIsOriginWorkspace,
    selectIsReadOnlyUser,
} from 'src/store/reducers/config.reducer';
import { ConfirmationService, MenuItem } from 'primeng/api';
import {
    DiagramFilterConfigService,
    DiagramFilterConfiguration,
} from 'src/services/diagram-filter-config.service';
import { FormControl, Validators } from '@angular/forms';
import { NavigationStart, Router } from '@angular/router';
import { Observable, Subscription, firstValueFrom } from 'rxjs';
import { animate, style, transition, trigger } from '@angular/animations';
import {
    updateChangeRequest,
    updateDiagramFilterConfig,
    updateSidebarVisible,
    updateSopDate,
} from 'src/store/actions/config.action';
import { AuthService } from 'src/services/auth.service';
import { ChangeRequest } from 'src/models/change-request-dto/change-request.dto';
import { ChangeRequestService } from 'src/services/basic-services/change-request.service';
import { NotificationService } from 'src/services/notification.service';
import { PANEL_ANIMATION_DURATION_MS } from '../diagrams/utils/const.util';
import { PendingChangesService } from 'src/services/pending-changes-service/pending-changes.service';
import { Store } from '@ngrx/store';
import { UserInfoState } from 'src/store/reducers/user-info.reducer';
import { filter } from 'rxjs/operators';
import { getSidebarItems } from './sidebar-items';
import { highlightInstanceInDrawer } from 'src/store/actions/tree-navigation.action';
import { refreshPendingChangesCount } from 'src/utils/pending-changes-count.util';
import { sopDateValidators } from 'src/validators/sop-date.validator';

// Dialogs
// eslint-disable-next-line sort-imports
import { CreateFilterConfigDialogComponent } from './create-filter-config-dialog/create-filter-config-dialog.component';
import { WorkspaceDialogComponent } from '../workspace-dialog/workspace-dialog.component';

@Component({
    selector: 'app-sidebar',
    templateUrl: './sidebar.component.html',
    styleUrls: ['./sidebar.component.scss'],
    animations: [
        trigger('slideInOutLeft', [
            transition(':enter', [
                style({ transform: 'translateX(-100%)', opacity: 0 }),
                animate(
                    `${PANEL_ANIMATION_DURATION_MS}ms ease`,
                    style({ transform: 'translateX(0)', opacity: 1 })
                ),
            ]),
            transition(':leave', [
                animate(
                    `${PANEL_ANIMATION_DURATION_MS}ms ease`,
                    style({ transform: 'translateX(-100%)', opacity: 0 })
                ),
            ]),
        ]),
    ],
    // eslint-disable-next-line @angular-eslint/prefer-standalone
    standalone: false,
})
export class SidebarComponent implements OnInit, OnDestroy {
    @ViewChild('createFilterConfigDialog')
    public createFilterConfigDialog!: CreateFilterConfigDialogComponent;
    @ViewChild('workspaceDialog') public workspaceDialog!: WorkspaceDialogComponent;

    // Inject
    public changeRequests: ChangeRequest[] = [];
    public changeRequestsHasMore = true;
    public changeRequestsLimit = 15;
    public diagramFilterConfigs: DiagramFilterConfiguration[] = [];
    public isChangeRequestsLoading = false;
    public isFilterConfigDialogLoading = false;
    public isFilterConfigsLoading = false;
    public isPendingChangesCountLoading = false;
    public isToolsExpanded = false;
    public isVisibleCRDialog = false;
    public isVisibleDemoDialog = false;
    public isVisibleFilterDialog = false;
    public isVisibleSOPDialog = false;
    public isVisibleSidebar = false;
    public isVisibleWorkspaceDialog = false;
    public selectedCR?: ChangeRequest;
    public showInactiveInTree = false;
    public sidebarItems: MenuItem[] = [];
    public userInitial = 'U';
    public username = 'username';
    public windowWidth!: number;

    // Forms
    // eslint-disable-next-line @typescript-eslint/member-ordering
    public crForm = new FormControl<number>(0, {
        nonNullable: false,
        validators: [Validators.required, Validators.pattern(/^\d*$/)],
    });

    public filterForm = new FormControl<number>(0, {
        nonNullable: false,
        validators: [Validators.required, Validators.pattern(/^\d*$/)],
    });

    public sopForm = new FormControl('', {
        nonNullable: true,
        validators: sopDateValidators(true),
    });

    public readonly PANEL_ANIMATION_DURATION_MS = PANEL_ANIMATION_DURATION_MS;

    private authService = inject(AuthService);
    private configSubscription?: Subscription;
    private confirmationService = inject(ConfirmationService);
    private crService = inject(ChangeRequestService);
    private currentWorkspaceKey?: number;
    private diagramFilterConfigService = inject(DiagramFilterConfigService);
    private notificationService = inject(NotificationService);
    private pendingChangesService = inject(PendingChangesService);
    private roleSubscription?: Subscription;
    private router = inject(Router);
    private store = inject<Store<{ config: ConfigState; userInfo: UserInfoState }>>(Store);
    private readonly NO_CONFIG_OPTION: DiagramFilterConfiguration = { id: 0, name: 'No config' };

    /* eslint-disable @typescript-eslint/member-ordering */
    public configs$: Observable<ConfigState> = this.store.select((state) => state.config);
    public isReadOnlyUser$ = this.store.select(selectIsReadOnlyUser);
    public isOriginWorkspace$ = this.store.select(selectIsOriginWorkspace);
    public pendingChangesCount$ = this.store.select((state) => state.config.pendingChangesCount);
    public selectedDiagramFilterConfig$ = this.store.select(
        (state) => state.config.diagramFilterConfig
    );
    public userInfo$: Observable<UserInfoState> = this.store.select((state) => state.userInfo);
    /* eslint-enable @typescript-eslint/member-ordering */

    public getTooltipText(): string {
        return `Username: ${this.username}`;
    }

    public getUserInitial(): string {
        return this.userInitial;
    }

    public ngOnDestroy(): void {
        this.configSubscription?.unsubscribe();
        this.roleSubscription?.unsubscribe();
    }

    public async ngOnInit(): Promise<void> {
        this.windowWidth = window.innerWidth;
        window.addEventListener('resize', () => (this.windowWidth = window.innerWidth));
        this.userInfo$.subscribe((userInfo) => {
            if (userInfo?.username) {
                this.username = userInfo.username;
                const parts = userInfo.username.split(/[\s._-]+/);
                if (parts.length >= 2) {
                    this.userInitial = parts[1][0].toUpperCase();
                } else {
                    this.userInitial = userInfo.username[0].toUpperCase();
                }
            }
        });

        this.router.events
            .pipe(filter((event) => event instanceof NavigationStart))
            .subscribe(() => {
                this.isVisibleCRDialog = false;
                this.isVisibleSOPDialog = false;
            });

        this.configSubscription = this.configs$.subscribe(this.onConfig.bind(this));
        this.roleSubscription = this.isReadOnlyUser$.subscribe(() => {
            this.updateSidebarItems();
        });

        this.pendingChangesCount$.subscribe(() => {
            this.isOriginWorkspace$
                .subscribe(() => {
                    this.updateSidebarItems();
                })
                .unsubscribe();
        });
    }

    public async onApplyFilterConfig(): Promise<void> {
        if (this.filterForm.value === null || this.filterForm.value === undefined) return;

        try {
            if (this.filterForm.value === 0) {
                await this.diagramFilterConfigService.clear();
                this.store.dispatch(
                    updateDiagramFilterConfig({ diagramFilterConfig: this.NO_CONFIG_OPTION })
                );
                this.notificationService.showSuccess('Filter configuration cleared');
            } else {
                await this.diagramFilterConfigService.apply(this.filterForm.value);
                const foundConfig = this.diagramFilterConfigs.find(
                    (config) => config.id === this.filterForm.value
                );
                if (foundConfig && foundConfig.id !== 0) {
                    this.store.dispatch(
                        updateDiagramFilterConfig({ diagramFilterConfig: foundConfig })
                    );
                    this.notificationService.showSuccess(`Filter "${foundConfig.name}" applied`);
                }
            }
        } catch (e) {
            this.notificationService.handleError('Failed to apply filter configuration', e);
        } finally {
            this.isVisibleFilterDialog = false;
        }
    }

    public async onClearChangeRequest(): Promise<void> {
        this.store.dispatch(updateChangeRequest({ changeRequest: undefined }));
        this.selectedCR = undefined;
        this.isVisibleCRDialog = false;
    }

    public async onClearFilterConfig(): Promise<void> {
        if (!this.filterForm.value) return;
        try {
            await this.diagramFilterConfigService.clear();
            this.store.dispatch(
                updateDiagramFilterConfig({ diagramFilterConfig: this.NO_CONFIG_OPTION })
            );
            this.notificationService.showSuccess('Filter configuration cleared');
            this.filterForm.reset();
        } catch (e) {
            this.notificationService.handleError('Failed to clear filter configuration', e);
        } finally {
            this.isVisibleFilterDialog = false;
        }
    }

    public async onCollapseSidebar() {
        this.isVisibleSidebar = false;
        this.store.dispatch(updateSidebarVisible({ sidebarVisible: false }));
    }

    public async onCreateFilterConfig(event: {
        configName: string;
        excludedKeys: number[];
    }): Promise<void> {
        const { configName, excludedKeys } = event;
        try {
            const newConfigId = await this.diagramFilterConfigService.create(
                configName,
                excludedKeys
            );

            if (newConfigId) {
                await this.diagramFilterConfigService.apply(newConfigId);
                const newConfig = { id: newConfigId, name: configName };
                this.store.dispatch(updateDiagramFilterConfig({ diagramFilterConfig: newConfig }));
            }

            await this.loadFilterConfigs();
            this.notificationService.showSuccess(
                'Filter configuration created and applied successfully'
            );
        } catch (error) {
            this.notificationService.handleError('Failed to create filter configuration', error);
        } finally {
            this.isVisibleFilterDialog = false;
        }
    }

    public async onDeleteFilterConfig(configId: number): Promise<void> {
        this.confirmationService.confirm({
            header: 'Delete filter configuration?',
            message: 'This action cannot be undone.',
            icon: 'pi pi-exclamation-triangle',
            rejectButtonStyleClass: 'p-button-text',
            acceptLabel: 'Delete',
            acceptButtonStyleClass: 'p-button-danger',
            rejectLabel: 'Cancel',
            rejectVisible: true,
            dismissableMask: true,
            accept: async () => {
                try {
                    await this.diagramFilterConfigService.delete(configId);
                    this.diagramFilterConfigs = this.diagramFilterConfigs.filter(
                        (config) => config.id !== configId
                    );

                    if (this.filterForm.value === configId) {
                        this.filterForm.reset();
                        this.store.dispatch(
                            updateDiagramFilterConfig({
                                diagramFilterConfig: this.NO_CONFIG_OPTION,
                            })
                        );
                    }
                    this.notificationService.showSuccess(
                        'Filter configuration deleted successfully'
                    );
                } catch (e) {
                    this.notificationService.handleError(
                        'Failed to delete filter configuration',
                        e
                    );
                }
            },
        });
    }

    public onInstanceSelected(event: { anchorKey: number; objectType: string }): void {
        this.store.dispatch(highlightInstanceInDrawer(event));
    }

    public async onLoadMoreChangeRequests(str?: string): Promise<void> {
        this.isChangeRequestsLoading = true;
        const newChangeRequests = await this.crService.get(
            this.changeRequestsLimit,
            this.changeRequests.length,
            str
        );
        this.changeRequests = [...this.changeRequests, ...newChangeRequests];
        this.changeRequestsHasMore = newChangeRequests.length >= this.changeRequestsLimit;
        this.isChangeRequestsLoading = false;
    }

    public async onOpenCRDialog() {
        this.changeRequests = [];
        await this.onLoadMoreChangeRequests();
        this.isVisibleCRDialog = true;
    }

    public async onSearchChangeRequests(str: string): Promise<void> {
        this.isChangeRequestsLoading = true;
        const searchStr = str?.trim() || undefined;
        const newChangeRequest = await this.crService.getByName(searchStr);
        this.changeRequests = newChangeRequest;
        this.changeRequestsHasMore = false;
        this.isChangeRequestsLoading = false;
    }

    public async onShowSidebar() {
        this.isVisibleSidebar = true;
        this.store.dispatch(updateSidebarVisible({ sidebarVisible: true }));
    }

    public showSignoutDialog() {
        this.confirmationService.confirm({
            header: 'Sign out?',
            message: 'You will be signed out.',
            icon: 'pi pi-sign-out',
            rejectButtonStyleClass: 'p-button-text',
            acceptLabel: 'Sign out',
            acceptButtonStyleClass: '',
            accept: () => this.signout(),
            rejectLabel: 'Cancel',
            rejectVisible: true,
            dismissableMask: true,
        });
    }

    public async signout(): Promise<void> {
        await this.authService.logout();
    }

    public toggleToolsExpansion(): void {
        this.isToolsExpanded = !this.isToolsExpanded;
        this.updateSidebarItems();
    }

    public async updateCr() {
        const configs = await firstValueFrom(this.configs$);
        if (this.crForm.value !== configs.changeRequest?.key) {
            const changeRequest = this.changeRequests.find((cr) => cr.key === this.crForm.value);
            this.store.dispatch(updateChangeRequest({ changeRequest: changeRequest }));
            this.selectedCR = changeRequest;
        }
        this.isVisibleCRDialog = false;
    }

    public async updateSop() {
        const configs = await firstValueFrom(this.configs$);
        if (this.sopForm.value !== configs.sopDate) {
            this.store.dispatch(updateSopDate({ sopDate: this.sopForm.value }));
        }
        this.isVisibleSOPDialog = false;
    }

    private async handlePendingChanges(isChildWorkspace?: boolean): Promise<void> {
        if (isChildWorkspace) {
            this.isPendingChangesCountLoading = true;
            this.updateSidebarItems();
            await refreshPendingChangesCount(this.store, this.pendingChangesService);
            this.isPendingChangesCountLoading = false;
            this.updateSidebarItems();
        }
    }

    private async loadFilterConfigs(): Promise<void> {
        this.isFilterConfigsLoading = true;
        const response = await this.diagramFilterConfigService.getAllFilterConfigs();
        this.diagramFilterConfigs = [this.NO_CONFIG_OPTION, ...response.configurations];
        this.filterForm.setValue(response.appliedConfigurationId ?? 0);
        const appliedConfig = response.configurations.find(
            (config) => config.id === response.appliedConfigurationId
        );
        this.store.dispatch(
            updateDiagramFilterConfig({
                diagramFilterConfig: appliedConfig || this.NO_CONFIG_OPTION,
            })
        );
        this.isFilterConfigsLoading = false;
    }

    private async onConfig(config: ConfigState): Promise<void> {
        this.selectedCR = config.changeRequest;
        this.isVisibleSidebar = config.sidebarVisible ?? false;
        this.sopForm.setValue(config.sopDate);
        this.crForm.setValue(config.changeRequest?.key ?? null);
        this.showInactiveInTree = config.showInactiveInTree;

        if (this.currentWorkspaceKey === config.workspace?.key) return;

        this.currentWorkspaceKey = config.workspace?.key;

        await this.loadFilterConfigs();
        await this.handlePendingChanges(!!config.workspace?.origin);
    }

    private updateSidebarItems(): void {
        const count = this.store.selectSignal((state) => state.config.pendingChangesCount)();
        const isOrigin = this.store.selectSignal(selectIsOriginWorkspace)();
        const isReadOnlyUser = this.store.selectSignal(selectIsReadOnlyUser)();
        const currentDB = this.store.selectSignal((state) => state.config.db)();
        const newItems = getSidebarItems(
            count || undefined,
            isOrigin,
            this.isPendingChangesCountLoading,
            this.isToolsExpanded,
            isReadOnlyUser,
            currentDB
        );

        const modelShapeChanged =
            this.sidebarItems.length !== newItems.length ||
            this.sidebarItems.some(
                (group, groupIndex) =>
                    (group.items?.length ?? 0) !== (newItems[groupIndex]?.items?.length ?? 0)
            );

        if (this.sidebarItems.length === 0 || modelShapeChanged) {
            this.sidebarItems = newItems;
        } else {
            this.sidebarItems.forEach((group, groupIndex) => {
                const newGroup = newItems[groupIndex];
                if (newGroup) {
                    Object.assign(group, {
                        badge: newGroup.badge,
                        isLoading: newGroup['isLoading'],
                        showDisabledChip: newGroup['showDisabledChip'],
                    });

                    if (group.items && newGroup.items) {
                        group.items.forEach((item, itemIndex) => {
                            const newItem = newGroup.items![itemIndex];
                            if (newItem) {
                                Object.assign(item, newItem);
                            }
                        });
                    }
                }
            });
        }
    }
}
